"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, Stethoscope, Phone, Search, Mail } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuLink } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="39932" className="hidden lg:flex items-center gap-8">
        {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-4 w-24 bg-muted animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu className="hidden lg:flex">
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-base font-medium text-foreground tracking-tight transition-colors hover:text-chart-2 focus:text-chart-2 focus:outline-none disabled:pointer-events-none disabled:opacity-50 relative overflow-hidden">
                <span className="relative z-10">{item.label}</span>
                <span className="absolute bottom-0 left-0 h-[2px] w-full origin-left scale-x-0 bg-chart-2 transition-transform duration-300 ease-out group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-md lg:hidden animate-in fade-in slide-in-from-top-5 duration-200 flex flex-col">
      <div className="container mx-auto px-4 py-6 flex flex-col h-full">
        <div className="flex items-center justify-between mb-8">
          <Link to="/HomePage" className="flex items-center gap-2" onClick={onClose}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603830/img/medispa_india_logo.png" alt="NeuroFamily" className="h-8 w-auto" />
          </Link>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-6 w-6 text-foreground" />
          </Button>
        </div>

        <nav className="flex-1 overflow-y-auto">
          <ul className="flex flex-col gap-4">
            {loading ? [1, 2, 3, 4].map(i => <li key={i} className="h-8 w-32 bg-muted animate-pulse rounded" />) : menuItems.map((item, i) => <li key={item.id} data-index={i} className="border-b border-border/40 pb-2">
                    <Link to={item.href || "#"} className="text-lg font-medium text-foreground hover:text-chart-2 transition-colors block py-2" onClick={onClose}>
                      {item.label}
                    </Link>
                  </li>)}
          </ul>
        </nav>

        <div className="mt-auto pt-8 pb-4 space-y-4">
          <Button className="w-full justify-center bg-primary text-primary-foreground hover:bg-primary/90 rounded-full shadow-lg" size="lg" asChild>
            <Link to="/ConnectPage">
              <Stethoscope className="mr-2 h-5 w-5 animate-pulse text-chart-2" />
              Request Appointment
            </Link>
          </Button>
          <div className="flex flex-col items-center gap-4">
            <div className="flex justify-center gap-6 text-muted-foreground">
              <Link to="tel:+1929357590" className="hover:text-chart-2 transition-colors">
                <Phone className="h-5 w-5" />
              </Link>
              <Link to="mailto:info@seanarminoncology.com" className="hover:text-chart-2 transition-colors">
                <Mail className="h-5 w-5" />
              </Link>
              <Button variant="ghost" size="icon" className="hover:text-chart-2 transition-colors h-auto p-0">
                <Search className="h-5 w-5" />
              </Button>
            </div>
            <div className="text-[10px] text-center text-muted-foreground uppercase tracking-widest px-4">
              1135 GLENDALE BLVD UNIT 5, LOS ANGELES, CA, 90026
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <MenuProvider menu_id="50" className="w-full">
      <header id="header" className={cn("sticky top-0 z-50 w-full border-b transition-all duration-300 ease-in-out", isScrolled ? "bg-background/80 backdrop-blur-md border-border/40 shadow-sm h-[70px]" : "bg-background border-border/20 h-[80px]")}>
        <div className="container mx-auto px-4 h-full flex items-center justify-between">
          
          <div className="flex-shrink-0">
            <Link to="/HomePage" className="flex items-center gap-2">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603830/img/medispa_india_logo.png" alt="NeuroFamily" className="h-10 w-auto object-contain transition-all duration-300" />
            </Link>
          </div>

          
          <div className="hidden lg:block flex-1 px-8">
            <div className="flex justify-center">
               <DesktopMenu />
            </div>
          </div>

          
          <div className="flex items-center gap-4">
            <div className="hidden lg:flex flex-col items-end gap-1">
              <div className="flex items-center gap-4">
                <Link to="tel:+1929357590" className="text-sm font-medium text-foreground hover:text-chart-2 transition-colors flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  <span className="hidden xl:inline">929-357-7590</span>
                </Link>
                <Link to="mailto:info@seanarminoncology.com" className="text-sm font-medium text-foreground hover:text-chart-2 transition-colors flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <span className="hidden xl:inline">info@seanarminoncology.com</span>
                </Link>
                <Button className="bg-primary hover:bg-primary/95 text-primary-foreground rounded-full shadow-md hover:shadow-[0_0_15px_rgba(15,23,42,0.2)] transition-all duration-300" asChild>
                  <Link to="/ConnectPage">
                    <Stethoscope className="mr-2 h-4 w-4 animate-pulse text-chart-2" />
                    Request Appointment
                  </Link>
                </Button>
              </div>
              <div className="text-[10px] text-muted-foreground/60 uppercase tracking-widest font-medium">
                1135 GLENDALE BLVD UNIT 5, LOS ANGELES, CA, 90026
              </div>
            </div>

            
            <Button variant="ghost" size="icon" className="lg:hidden text-foreground" onClick={() => setIsMobileMenuOpen(true)}>
              <Menu className="h-6 w-6" />
              <span className="sr-only">Open menu</span>
            </Button>
          </div>
        </div>

        
        <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </header>
    </MenuProvider>;
}